import type { LogSignatureList } from "@/hooks/api";

const nf = new Intl.NumberFormat("nb-NO");

function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2).replace(".", ",")}M`;
  if (n >= 10_000) return `${Math.round(n / 1000)}k`;
  return nf.format(n);
}

interface CardProps {
  label: string;
  swatch: string;
  className: string;
  value: string;
  valueClass: string;
  secondary: string;
  footer: string;
  footerClass: string;
  pill?: string;
}

function Card({
  label,
  swatch,
  className,
  value,
  valueClass,
  secondary,
  footer,
  footerClass,
  pill,
}: CardProps) {
  return (
    <div className={`rounded-[10px] border px-4 py-3.5 flex flex-col gap-2 ${className}`}>
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-[2px] ${swatch}`} />
        <span className="text-[11px] font-semibold tracking-[0.06em]">{label}</span>
        {pill && (
          <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-admin-slate-badge text-admin-text-subtle">
            {pill}
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-2.5">
        <span className={`text-[30px] font-bold leading-none ${valueClass}`}>{value}</span>
        <span className="text-[13px] text-admin-text-secondary">{secondary}</span>
      </div>
      <div className={`text-xs ${footerClass}`}>{footer}</div>
    </div>
  );
}

export function SummaryCards({ data }: { data: LogSignatureList }) {
  const { counts, events, newCounts, topAuth, infoDebugEstimate } = data;
  // newCounts and topAuth come from the server, computed before the bucket
  // filter — deriving them from `signatures` here would make the cards report
  // on whichever chip happens to be active.
  const newCount = newCounts.action;

  return (
    <div className="grid grid-cols-[1fr_1fr_1fr_0.8fr] gap-3">
      <Card
        label="KREVER HANDLING"
        swatch="bg-admin-danger-400"
        className="bg-admin-danger-500/[0.06] border-admin-danger-500/25"
        value={nf.format(counts.action)}
        valueClass="text-admin-danger-400"
        secondary={`signaturer · ${nf.format(events.action)} hendelser`}
        footer={
          newCount > 0 ? `${newCount} nye siste døgn` : "Ingen nye siste døgn"
        }
        footerClass={newCount > 0 ? "text-admin-primary" : "text-admin-text-subtle"}
      />

      <Card
        label="AVVIST · AUTH 401/403"
        swatch="bg-admin-warn-400"
        className="bg-admin-warn-500/[0.06] border-admin-warn-500/25"
        value={nf.format(events.auth)}
        valueClass="text-admin-warn-400"
        secondary={`${counts.auth} signaturer`}
        footer={topAuth ? `Størst: ${topAuth}` : "Ingen avviste forespørsler"}
        footerClass="text-admin-text-secondary truncate"
      />

      <Card
        label="SKANNING · 404"
        swatch="bg-admin-slate"
        className="bg-admin-card border-admin-card-border"
        value={nf.format(events.noise)}
        valueClass="text-admin-text-subtle"
        secondary={`${counts.noise} mønstre`}
        footer="Ingen traff en registrert rute"
        footerClass="text-admin-text-subtle"
        pill="KARANTENE"
      />

      <div className="rounded-[10px] border border-admin-card-border bg-admin-card px-4 py-3.5 flex flex-col gap-2">
        <div className="text-[11px] font-semibold tracking-[0.06em] text-admin-text-subtle">
          INFO / DEBUG
        </div>
        <div className="text-[22px] font-semibold leading-none text-admin-text-secondary">
          {compact(infoDebugEstimate)}
        </div>
        <div className="text-xs text-admin-text-subtle">
          Skjult · kun konsoll i prod
        </div>
      </div>
    </div>
  );
}
